import type { MetaFunction } from "react-router";
import { Box, Container, Typography, Divider, Grid } from '@mui/material';
import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const etopediePhotos = [
  '/img/etopedie/IMG_20251010_134852.webp',
  '/img/etopedie/IMG_20251010_135225.webp',
  '/img/etopedie/IMG_20251010_135405.webp',
  '/img/etopedie/IMG_20251010_135542.webp',
  '/img/etopedie/IMG_20251010_135841.webp',
  '/img/etopedie/IMG_20251010_140801.webp',
  '/img/etopedie/IMG_20251010_134919.webp',
];

export const meta: MetaFunction = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Etopedické poradenství",
    "description": "Etopedické poradenství v Bílovci a okolí. Pomoc při poruchách chování, výchovných problémech. Podpora pro děti, dospívající a rodiče v náročných situacích.",
    "url": "https://www.hanajarkovska.cz/etopedicke-poradenstvi",
    "image": "https://www.hanajarkovska.cz/img/etopedie/IMG_20251010_134852.webp",
    "provider": {
      "@type": "Person",
      "name": "Mgr. Hana Jarkovská",
      "jobTitle": "Etoped",
      "url": "https://www.hanajarkovska.cz"
    },
    "areaServed": [
      { "@type": "City", "name": "Bílovec" },
      { "@type": "City", "name": "Nový Jičín" },
      { "@type": "City", "name": "Ostrava" },
      { "@type": "AdministrativeArea", "name": "Moravskoslezský kraj" }
    ],
    "serviceType": "Etopedické poradenství",
    "category": "Speciálně pedagogické služby",
    "audience": {
      "@type": "PeopleAudience",
      "audienceType": "Děti, dospívající, rodiče, rodiny"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Bílovec",
          "postalCode": "743 01",
          "addressCountry": "CZ"
        }
      }
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "CZK"
    }
  };

  return [
    { tagName: "link", rel: "canonical", href: "https://www.hanajarkovska.cz/etopedicke-poradenstvi" },
    { title: "Etopedické poradenství — Mgr. Hana Jarkovská" },
    { name: "description", content: "Etopedické poradenství v Bílovci a okolí. Pomoc při poruchách chování, výchovných problémech. Podpora pro děti, dospívající a rodiče v náročných situacích." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://www.hanajarkovska.cz/etopedicke-poradenstvi" },
    { property: "og:title", content: "Etopedické poradenství — Mgr. Hana Jarkovská" },
    { property: "og:description", content: "Etopedické poradenství v Bílovci a okolí. Pomoc při poruchách chování, výchovných problémech. Podpora pro děti, dospívající a rodiče v náročných situacích." },
    { property: "og:image", content: "https://www.hanajarkovska.cz/img/etopedie/IMG_20251010_134852.webp" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Etopedické poradenství — Mgr. Hana Jarkovská" },
    { name: "twitter:description", content: "Etopedické poradenství v Bílovci a okolí. Pomoc při poruchách chování, výchovných problémech. Podpora pro děti, dospívající a rodiče v náročných situacích." },
    { name: "twitter:image", content: "https://www.hanajarkovska.cz/img/etopedie/IMG_20251010_134852.webp" },
    { "script:ld+json": structuredData },
  ];
};

export default function EtopediaPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="md">
          <Typography variant="h1" sx={{ mb: 2 }}>
            Etopedie
          </Typography>
          <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 400, mb: 4 }}>
            Když terapie nestačí
          </Typography>

          <Box sx={{ '& > p': { mb: 2.5 } }}>
            <Typography sx={{ textAlign: 'justify' }}>
              Psychoterapie nabízí různé přístupy – mluví, naslouchá, vnímá tělo, emoce, trauma i vztahy. Přesto však nastávají situace, kdy samotná terapie nestačí.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Například když je třeba pracovat s konkrétním chováním v běžném životě. Když člověk nechápe, co se s ním děje – a nedokáže to ještě pojmenovat, potřebuje základní stabilitu. Když zatím nedokáže navázat vztah, který by umožnil terapeutickou práci. Když odmítá spolupracovat nebo reaguje způsobem, který jeho okolí nechápe, nezvládá – ale za tím není „zlý úmysl", jen nepochopení, bolest nebo zmatek.
            </Typography>

            <Box sx={{ textAlign: 'justify' }}>
              <Box
                component="img"
                src="/img/etopedie/IMG_20251010_134852.webp"
                alt="Historické materiály o etopedii - dokumenty z vývoje oboru"
                loading="lazy"
                onClick={() => openLightbox(0)}
                sx={{
                  width: { xs: '100%', sm: '40%' },
                  height: 'auto',
                  float: { xs: 'none', sm: 'right' },
                  ml: { xs: 0, sm: 3 },
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              />
              <Typography component="span" sx={{ display: 'block' }}>
                Právě tady pomáhá etopedie. Je to jiný způsob podpory, často víc strukturovaný a někdy intervenční. Je víc spojená s prostředím, pravidly a hranicemi, ale zároveň i s důvěrou a přijetím. Pomáhá pochopit, co chování vyjadřuje, a hledá způsoby, jak ho změnit tak, aby to bylo snesitelné pro člověka i jeho okolí.
              </Typography>
              <Typography component="span" sx={{ display: 'block', mt: 2.5 }}>
                Etopedie pracuje tam, kde je potřeba přinést jasnou předvídatelnost, bezpečí nebo vedení. Je to prostor pro změnu skrze vztah, pravidla, důsledky – ale i přirozenost, přijetí a lidskost.
              </Typography>
            </Box>

            <Typography sx={{ textAlign: 'justify' }}>
              Každé chování má svůj důvod. Ať už jde o dítě, dospívajícího nebo dospělého člověka, určité projevy mohou být výrazem vnitřního napětí, dlouhodobého stresu, nezpracovaných emocí nebo tlaku, se kterým si dotyčný nedokáže poradit jinak. Reakce, které jsou pro okolí náročné nebo těžko pochopitelné, často ukazují na vnitřní nepohodu, přetížení, nezpracované zkušenosti nebo chybějící dovednosti. Může jít o impulzivitu, uzavření se, výbuchy emocí, odpor ke změnám nebo neschopnost navazovat vztahy – signály, které si zaslouží pozornost, ne odsouzení. To, co navenek působí jako vzdor, agresivita nebo chaos, může být ve skutečnosti zprávou.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Etopedie nabízí způsob, jak tyhle signály chápat. Místo odsuzování hledáme souvislosti – v osobní historii, rodinném systému, naučených vzorcích chování, vnitřním nastavení i prostředí, které člověka obklopuje. Pomáhá zjistit, co chování říká, co člověk potřebuje a jak na to citlivě reagovat. Cílem je často uklidnit vnitřní zmatek, obnovit důvěru v sebe sama, lépe zvládat tlak nebo najít vlastní směr. Pomáhá vytvářet podmínky, ve kterých člověk může začít reagovat jinak – vědoměji, s větším respektem k sobě i okolí.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Etopedie zůstává pro mnoho lidí poměrně neznámým pojmem. Právě proto má smysl nahlédnout{' '}
              <Box
                component="a"
                href="#historie"
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('historie');
                  if (element) {
                    const headerOffset = 100;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                sx={{
                  color: 'inherit',
                  textDecoration: 'underline',
                  textDecorationColor: 'rgba(0, 0, 0, 0.3)',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecorationColor: 'inherit',
                  },
                }}
              >
                do její historie
              </Box>
              , která přináší řadu zajímavých momentů i osobností, jež ovlivnily její vývoj. Pohled do minulosti ukazuje, jak se společnost postupně učila vnímat potřeby lidí, jejichž chování vybočovalo z očekávaných norem – a jak hledala cesty, jak s nimi být v kontaktu citlivěji a vstřícněji k jejich skutečným potřebám.
            </Typography>

            <Grid container spacing={2} sx={{ my: 5 }}>
              {etopediePhotos.slice(1).map((photo, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                  <Box
                    component="img"
                    src={photo}
                    alt="Materiály pro etopedickém poradenství"
                    loading="lazy"
                    onClick={() => openLightbox(index + 1)}
                    sx={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'cover',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 6, opacity: 0.3, borderColor: 'primary.main' }} />

            <Typography variant="h3" id="historie" sx={{ mb: 3, fontSize: '1.5rem', fontWeight: 500 }}>
              Historie
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Obor má historické základy. V českých zemích byl název etopedie odvozen z řeckých slov éthos (mrav, zvyk) a paideia (výchova). Tento termín se však ustálil pouze v rámci úzké odborné komunity, mimo ni je veřejnosti často neznámý. V mezinárodním kontextu se navíc pojem etopedie vůbec nepoužívá – přestože se obdobné směry a přístupy rozvíjely i v zahraničí, byť pod jinými názvy.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Ačkoli se samotný pojem etopedie objevil až v moderní době, problémy spojené s chováním, které se vymyká společensky přijatelným normám, provázejí lidstvo od počátku jeho existence. Důkazem toho je i temný obraz středověkých „lodí bláznů", na nichž byli lidé s psychickými obtížemi nebo odlišným chováním symbolicky i doslova vyčleňováni ze společnosti a vysíláni na cestu bez cíle. Tento přístup, založený na vyloučení a stigmatizaci, byl v mnoha obdobích běžný a ukazuje, jak hluboko sahá lidská nejistota vůči jinakosti.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Již ve starověku se formovaly první představy o tom, co je „mravné" chování, a jak vychovávat jedince k odpovědnosti a sebekázni.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Ve Spartě byla výchova postavena na přísné kázni a tvrdém sebeovládání, často za použití drastických metod, které měly dítě připravit na vojenský a disciplinovaný život. Naproti tomu čínský filozof Konfucius kladl důraz na morální výchovu jako klíč k harmonické společnosti – podle něj bylo pěstování ctností cestou k rozumnému uspořádání mezilidských vztahů. Řecký filozof Platón pak chápal morální selhání jako důsledek neznalosti dobra a zdůrazňoval význam výchovy pro dosažení ctnosti.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              V renesanční Evropě začíná reflexe výchovy nabývat konkrétnějších podob. Desiderius Erasmus Rotterdamský se ve svém spise O uhlazenosti dětských mravů zamýšlel nad formováním charakteru dítěte, a Michel de Montaigne upozorňoval na zhoubné následky příliš tvrdé, despotické výchovy. V 17. století pak Jan Amos Komenský požadoval, aby se školy staly „dílnami lidskosti", kde se dítě rozvíjí nejen po stránce intelektuální, ale i mravní.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              John Locke zastával názor, že ovládání a sebekázeň by měly být u dítěte pěstovány už od nejútlejšího věku. O století později Jean-Jacques Rousseau ve své přirozené výchově zdůrazňoval, že dítě má na sobě samo pocítit důsledky svého nevhodného chování – právě tyto přirozené následky měly vést k nápravě, nikoli trest.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Významnou postavou pedagogického myšlení byl i Johann Friedrich Herbart, podle nějž je vedení dítěte klíčovým předpokladem výchovné a vzdělávací činnosti, zejména v raném věku. V 19. a 20. století se začínají objevovat první systematické snahy porozumět dětem s výraznějšími výchovnými problémy. Například William Healy se zabýval kriminalitou mladistvých, zatímco C. L. Burt zkoumal příčiny delikvence.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Rakouský psycholog Alfred Adler pak rozšířil pohled na „těžce vychovatelné děti" – věnoval se například problémům spojeným s neurózami, psychózami či poruchami chování. Upozorňoval na souvislost mezi výchovou, sociálním prostředím a vznikem psychických obtíží nebo delikventního chování.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Adlerovy myšlenky, i když vycházely z psychologie, rozšiřovaly chápání problémového chování směrem k širším sociálním a výchovným souvislostem. Právě to dnes ukazuje, že ne všechny projevy chování, které vybočují z běžných norem, lze uchopit čistě psychologickou optikou. Ačkoliv je psychologie veřejností často vnímána jako hlavní disciplína zabývající se poruchami chování, její reálné zaměření a kompetence se v praxi výrazně liší. Klinický psycholog se specializuje na diagnostiku a léčbu duševních poruch, zatímco školní nebo poradenský psycholog se soustředí zejména na podporu a orientační posouzení potíží v rámci výchovně-vzdělávacího prostředí.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Psychologové se sice mohou setkávat s konkrétními projevy chování v každodenních situacích – například ve škole nebo v poradenském kontextu, jejich přístup však obvykle nesměřuje k systematické a dlouhodobé práci s těmito projevy v širším prostředí člověka. Zaměřují se spíše na posouzení, poradenství či doporučení další péče. Otázky jako zvládání konfliktů, nastavování hranic nebo práce s chováním, které narušuje soužití, často přesahují rámec jejich běžné praxe.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Přesto přetrvává rozšířený omyl, že každý psycholog automaticky poskytuje psychoterapii. Psychoterapie však vyžaduje samostatné, dlouhodobé vzdělání v rámci specializovaného výcviku a může ji vykonávat pouze odborník, který tímto výcvikem prošel – bez ohledu na to, zda je psychologem, lékařem či jiným pomáhajícím profesionálem.
            </Typography>

            <Typography sx={{ textAlign: 'justify' }}>
              Etopedie se v tomto kontextu profiluje jako prakticky orientovaný obor, který se zaměřuje na práci s projevy chování přímo v jejich reálném prostředí. Nejde jen o zkoumání příčin, ale o hledání konkrétních cest, jak pomoci člověku zvládat obtížné situace a vztahy.
            </Typography>

            <Typography sx={{ textAlign: 'justify', mb: 5 }}>
              Jde o schopnost vstoupit do každodenního života člověka a aktivně mu pomáhat překonávat obtíže, které z jeho chování vyplývají – ať už jde o selhávání v mezilidských vztazích, napětí nebo opakované konflikty.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={etopediePhotos.map((photo) => ({ src: photo }))}
      />
    </>
  );
}
